import * as React from "react";
import { Schemas, config, constants, Routes, Copy } from "utils";
import { stringReplacer } from "utils/helpers/string";
import { mapGoogleAddressComponents } from "utils/helpers/geo";
import { FETCH_LOCATIONS, FETCH_PLACES } from "utils/api";
import { DeliverySearch } from "components/templates/AddressSearch/DeliverySearch";
import { PickupKioskSearch } from "components/templates/AddressSearch/PickupKioskSearch";
import { useLocationState } from "hooks";
import { useOrderContext } from "components/providers/Order/OrderContext";
import {
  getAddress,
  getLocationsWithRestaurantByIds,
  sortLocationByDistance,
  sortLocationByState,
} from "./utils";
import css from "./locationsMap.module.scss";

const restaurantName = config.restaurant;
const googleMapKey =
  config.apps.google_maps || "AIzaSyDDp__7dinJ-_hqnj9iBjFD6acYRrY46I4";
const isMarketPlace = config?.market_place?.enabled;
// Tabs
const {
  ORDER_TYPES: { PICKUP, DELIVERY, KIOSK },
} = constants;
const tabs = {
  [DELIVERY]: config?.delivery?.enabled && {
    diningOption: DELIVERY,
    form: ({ deliveryChange, clearLocations, address, handleStreetChange }) => (
      <DeliverySearch
        onSelect={deliveryChange}
        onFocus={clearLocations}
        onError={clearLocations}
        address={address}
        handleStreetChange={handleStreetChange}
      />
    ),
    name: "Delivery",
  },
  [KIOSK]: config?.kiosk?.enabled && {
    diningOption: KIOSK,
    form: ({ getGeo, getPickupGeo }) => (
      <PickupKioskSearch onGeo={getGeo} onSelect={getPickupGeo} />
    ),
    name: Copy.LOCATIONS_STATIC.LOCATION_KIOSK_TEXT,
  },
  [PICKUP]: config?.pickup?.enabled && {
    diningOption: PICKUP,
    form: ({ getGeo, getPickupGeo }) => (
      <PickupKioskSearch onGeo={getGeo} onSelect={getPickupGeo} />
    ),
    name: "Pickup",
  },
};
const tabOrder = config?.tab_order.map((tab) => tabs[tab]).filter((tab) => tab);

const orderTypeMap = {
  delivery: "delivery",
  kiosk: "kiosk",
  pickup: "pickup",
};

const withLocation =
  (Component) =>
  ({
    order,
    patron: {
      updateAddress,
      patron: { addresses = [], firstName = "" },
    },
    changeLocation,
    menu,
    setOrderType,
    setDeliveryAddress,
    ...props
  }) => {
    const [diningOption, setDiningOption] = React.useState(order.diningOption);
    const locationCardRefs = React.useRef({});
    const [selectedRestaurants, setSelectedRestaurants] = React.useState([]);
    const orderContext = useOrderContext();

    const {
      locationState,
      setLocationState,
      clearLocations,
      deliveryChange,
      deliveryFetchCount,
      locations,
      handleStreetChange,
      setFetching,
      setLocations,
      fetching,
      restaurants,
      filteredLocations,
      setFilteredLocations,
    } = useLocationState(diningOption);

    const locationTitle = Copy.RG_INFO_STATIC.LOCATION_PAGE_HEADER_TEXT ? (
      stringReplacer(Copy.RG_INFO_STATIC.LOCATION_PAGE_HEADER_TEXT, [
        {
          replaceTarget: "{firstName}",
          replaceValue: firstName ? `, ${firstName}` : ``,
        },
      ])
    ) : (
      <h1 className={css["locationsMap-info--locationHeader"]}>
        {/* According to James Davey role="text" is a valid role. Disabling linting for this line */}
        {/* eslint-disable-next-line jsx-a11y/aria-role */}
        <span role="text">
          Find your nearest
          <br />
          {restaurantName}
        </span>
      </h1>
    );
    React.useEffect(() => {
      locationCardRefs.current = {};
    }, [locations]);

    React.useEffect(() => {
      setLocations([]);
    }, [diningOption]);

    const onTabChange = (nextTab) => {
      const { address } = locationState;
      const isValidDelivery =
        nextTab === "delivery" && Schemas.AddressSchema.isValidSync(address);
      if (isValidDelivery) {
        setFetching(true);
      }
      setDiningOption(nextTab);
    };

    React.useEffect(() => {
      if (selectedRestaurants.length) {
        setFilteredLocations(
          getLocationsWithRestaurantByIds({
            locations,
            restaurantIds: selectedRestaurants,
          }),
        );
      } else {
        setFilteredLocations(locations);
      }
    }, [selectedRestaurants]);

    const onClickMarker = (id) => {
      if (locationCardRefs.current[id]) {
        locationCardRefs.current[id].scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    };

    const getGeo = (geo) => {
      const { lat, long } = locationState;
      const newLocationState = { ...locationState, addressComponents: {} };
      if (geo.lat !== lat || geo.long !== long) {
        newLocationState.lat = geo.lat;
        newLocationState.long = geo.long;
      }
      setLocationState(newLocationState);
    };

    const getPickupGeo = async (placeId) => {
      setFetching(true);
      try {
        const { data } = await FETCH_PLACES(placeId, { orderType: "pickup" });
        setLocationState({
          ...locationState,
          addressComps: mapGoogleAddressComponents(data.addressComponents),
          lat: data.lat,
          long: data.long,
        });
      } catch (error) {
        console.error(error);
      }
    };

    const displayAddressForm =
      diningOption === PICKUP ? locations.length > 2 : true;

    const onLocationSelected = (location, params) => {
      const locationHandle = location?.slug?.toLowerCase();
      const locationRoute = `${Routes.LOCATION_INFO({
        orderType: orderTypeMap[diningOption],
        resGroupId: location.restaurantGroup,
        slug: locationHandle,
      })}`;
      changeLocation(location);
      setOrderType(diningOption);
      setDeliveryAddress(locationState.address);
      updateAddress(locationState.address);

      if (isMarketPlace) {
        props.history.push(
          Routes.MARKET_PLACE(location.slug, orderTypeMap[diningOption]),
        );
        return;
      }
      if (params) {
        props.history.push(`${locationRoute}?${params}`);
        return;
      }
      props.history.push(locationRoute);
    };

    const onMarketplaceLocationSelected = (location, params = "") => {
      orderContext.setOrderType(diningOption);
      changeLocation(location);
      if (diningOption === DELIVERY) {
        orderContext.setDeliveryAddress(locationState.address);
      }
      props.history.push(
        `${Routes.LOCATION_INFO({
          orderType: orderTypeMap[diningOption],
          resGroupId: location.restaurantGroups[0].id,
          slug: location.slug,
        })}?${params}`,
      );
    };

    const returnProps = {
      deliveryChange,
      deliveryFetchCount,
      diningOption,
      displayAddressForm,
      fetching,
      filteredLocations,
      firstName,
      getAddress,
      getGeo,
      getPickupGeo,
      googleMapKey,
      handleStreetChange,
      isMarketPlace,
      locationCardRefs,
      locations,
      locationState,
      locationTitle,
      onClickMarker,
      onLocationSelected,
      onMarketplaceLocationSelected,
      onTabChange,
      restaurants,
      selectedRestaurants,
      setDiningOption,
      setLocationState,
      setSelectedRestaurants,
      sortLocationByDistance,
      sortLocationByState,
      tabOrder,
      tabs,
      updateAddress,
    };

    return <Component {...props} {...returnProps} />;
  };

export default withLocation;
